import { useState } from 'react';
import { Check, X, MessageSquare, Clock, Inbox, ArrowRight } from 'lucide-react';
import {
  usePendingLeaves,
  useLeaveRequests,
  useApproveLeave,
  useRejectLeave,
} from '@/lib/queries';
import { initials, formatDateOnly } from '@isoft-people/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageTransition, FadeIn, StaggerList, StaggerItem } from '@/components/ui/motion';

type Tab = 'pending' | 'approved' | 'rejected' | 'all';

const TABS: { key: Tab; label: string }[] = [
  { key: 'pending', label: 'Pendientes' },
  { key: 'approved', label: 'Aprobadas' },
  { key: 'rejected', label: 'Rechazadas' },
  { key: 'all', label: 'Todas' },
];

const AVATAR_COLORS = [
  'from-primary to-primary-light',
  'from-accent to-green-400',
  'from-warning to-yellow-400',
  'from-error to-pink-400',
  'from-info to-cyan-400',
  'from-purple-500 to-indigo-400',
];

const TYPE_LABELS: Record<string, string> = {
  VACATION: 'Vacaciones',
  MEDICAL: 'Permiso médico',
  PERSONAL: 'Permiso personal',
  BEREAVEMENT: 'Duelo',
  MATERNITY: 'Maternidad',
  PATERNITY: 'Paternidad',
  STUDY: 'Estudios',
  OTHER: 'Otro',
};

const STATUS_MAP: Record<
  string,
  { variant: 'success' | 'warning' | 'error'; label: string }
> = {
  APPROVED: { variant: 'success', label: 'Aprobada' },
  PENDING: { variant: 'warning', label: 'Pendiente' },
  REJECTED: { variant: 'error', label: 'Rechazada' },
};

export function ManagerInboxPage() {
  const [tab, setTab] = useState<Tab>('pending');
  const { data: pending } = usePendingLeaves();
  const { data: all } = useLeaveRequests({});
  const approve = useApproveLeave();
  const reject = useRejectLeave();

  const items =
    tab === 'pending'
      ? pending?.items
      : tab === 'approved'
      ? all?.items?.filter((l: any) => l.status === 'APPROVED')
      : tab === 'rejected'
      ? all?.items?.filter((l: any) => l.status === 'REJECTED')
      : all?.items;

  const pendingCount = pending?.items?.length ?? 0;

  const handleReject = async (id: string) => {
    const reason = prompt('Motivo del rechazo:');
    if (!reason) return;
    await reject.mutateAsync({ id, reason });
  };

  return (
    <PageTransition className="flex flex-col gap-6">
      {/* Header con acento de marca */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-subtle">
            <Inbox size={22} className="text-primary" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-font-primary">Bandeja de Aprobaciones</h1>
            <p className="mt-0.5 text-sm text-font-secondary">
              Revisa y gestiona las solicitudes de tu equipo
            </p>
          </div>
        </div>
        {pendingCount > 0 && (
          <Badge variant="warning" dot>
            {pendingCount} pendientes
          </Badge>
        )}
      </div>

      <FadeIn delay={0.05}>
        <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
          <div className="flex flex-wrap gap-1 border-b border-border px-6">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`relative px-4 py-4 text-sm font-semibold transition ${
                  tab === t.key ? 'text-primary' : 'text-font-secondary hover:text-font-primary'
                }`}
              >
                {t.label}
                {tab === t.key && (
                  <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t bg-primary" />
                )}
              </button>
            ))}
          </div>

          <StaggerList className="divide-y divide-border-light">
            {items?.map((lr: any, i: number) => {
              const color = AVATAR_COLORS[i % AVATAR_COLORS.length];
              const name = `${lr.employee.firstName} ${lr.employee.lastName}`;
              const statusInfo = STATUS_MAP[lr.status];
              const fmt = (d: string) => formatDateOnly(d);
              return (
                <StaggerItem
                  key={lr.id}
                  className="flex flex-wrap items-center gap-4 p-5 transition-colors hover:bg-primary-subtle/40"
                >
                  <div
                    className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${color} text-sm font-semibold text-white shadow-sm`}
                  >
                    {initials(name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-font-primary">{name}</p>
                      <span className="rounded-full bg-bg px-2 py-0.5 text-[11px] text-font-secondary">
                        {TYPE_LABELS[lr.type] ?? lr.type}
                      </span>
                      {tab !== 'pending' && statusInfo && (
                        <Badge variant={statusInfo.variant} dot>
                          {statusInfo.label}
                        </Badge>
                      )}
                    </div>
                    <p className="mt-1 inline-flex items-center gap-1 text-xs text-font-secondary">
                      {fmt(lr.startDate)}
                      <ArrowRight size={12} className="text-font-tertiary" />
                      {fmt(lr.endDate)} ·{' '}
                      <span className="font-semibold">{lr.workingDays} días</span>
                    </p>
                    <p className="mt-1 text-xs text-font-tertiary">
                      <span className="font-semibold">Motivo:</span>{' '}
                      {lr.reason ?? 'Sin descripción'}
                    </p>
                  </div>

                  {tab === 'pending' && (
                    <div className="flex items-center gap-1.5">
                      <span className="mr-1 flex items-center gap-1 rounded-full bg-warning/10 px-2 py-1 text-[10px] font-semibold text-warning">
                        <Clock size={10} />
                        Hace 2 días
                      </span>
                      <Button
                        variant="success"
                        size="icon"
                        onClick={() => approve.mutate(lr.id)}
                        title="Aprobar"
                      >
                        <Check size={16} />
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleReject(lr.id)}
                        title="Rechazar"
                      >
                        <X size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" title="Comentar">
                        <MessageSquare size={16} />
                      </Button>
                    </div>
                  )}
                </StaggerItem>
              );
            })}
            {(!items || items.length === 0) && (
              <div className="flex flex-col items-center gap-2 py-16">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bg">
                  <Inbox size={22} className="text-font-tertiary" />
                </span>
                <p className="text-sm font-medium text-font-secondary">
                  Sin solicitudes en esta categoría
                </p>
              </div>
            )}
          </StaggerList>
        </div>
      </FadeIn>
    </PageTransition>
  );
}
